import React, { useState } from 'react';
import styled from 'styled-components';
import { DragDropContext } from 'react-beautiful-dnd';
import './App.css';

// Component imports
import Navbar from './components/common/Navbar';
import Sidebar from './components/sidebar/Sidebar';
import Editor from './components/editor/Editor';
import PropertiesPanel from './components/properties/PropertiesPanel';
import { TemplateProvider, useTemplate } from './context/TemplateContext';

function AppContent() {
  const [selectedElement, setSelectedElement] = useState(null);
  const { addElement, reorderElements } = useTemplate();

  // Handle drag end events
  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    
    // Only handle reordering within the editor
    if (source.droppableId === 'editor' && destination.droppableId === 'editor') {
      reorderElements(source.index, destination.index);
    }
  };

  // Helper function to create a new element based on type
  const createElementFromType = (type) => {
    switch (type) {
      case 'header':
        return {
          type: 'header',
          content: 'Your Header Text',
          styles: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#333333',
            textAlign: 'center',
            padding: '20px'
          }
        };
      case 'text':
        return {
          type: 'text',
          content: 'Your paragraph text goes here. Edit this to add your own content.',
          styles: {
            fontSize: '16px',
            color: '#666666',
            textAlign: 'left',
            padding: '10px 20px'
          }
        };
      case 'image':
        return {
          type: 'image',
          src: 'https://via.placeholder.com/600x200',
          alt: 'Image description',
          defaultSrc: 'https://via.placeholder.com/600x200',
          defaultAlt: 'Placeholder image',
          width: '100%',
          styles: {
            textAlign: 'center',
            padding: '10px'
          }
        };
      case 'button':
        return {
          type: 'button',
          content: 'Click Here',
          href: '#',
          styles: {
            buttonColor: '#5876ff',
            textColor: '#ffffff',
            fontSize: '16px',
            fontWeight: 'bold',
            borderRadius: '4px',
            textAlign: 'center',
            padding: '15px'
          }
        };
      case 'spacer':
        return {
          type: 'spacer',
          height: '30px'
        };
      case 'divider':
        return {
          type: 'divider',
          styles: {
            color: '#dddddd',
            padding: '10px 0'
          }
        };
      case 'footer':
        return {
          type: 'footer',
          content: ' 2025 Your Company. All rights reserved.',
          includeUnsubscribe: true,
          styles: {
            backgroundColor: '#f5f5f5',
            fontSize: '12px',
            color: '#666666',
            textAlign: 'center',
            padding: '20px'
          }
        };
      default:
        return { type: 'unknown' };
    }
  };

  // Handle adding elements from the sidebar
  const handleAddElement = (type) => {
    const newElement = createElementFromType(type);
    addElement(newElement);
    setSelectedElement(newElement);
  };

  // Ref for the hidden HTML container
  const htmlContainerRef = React.useRef();

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <AppContainer>
        <Navbar htmlContainerRef={htmlContainerRef} />
        <MainContent>
          <Sidebar onAddElement={handleAddElement} />
          <Editor onSelectElement={setSelectedElement} selectedElement={selectedElement} />
          <PropertiesPanel selectedElement={selectedElement} />
        </MainContent>
        {/* Hidden container for rendered HTML output */}
        <div
          ref={htmlContainerRef}
          style={{ position: 'absolute', left: '-9999px', top: 0, opacity: 0, pointerEvents: 'none' }}
          aria-hidden="true"
          tabIndex={-1}
          id="hidden-html-output"
          dangerouslySetInnerHTML={{ __html: useTemplate().generateHTML() }}
        />
      </AppContainer>
    </DragDropContext>
  );
}

function App() {
  return (
    <TemplateProvider>
      <AppContent />
    </TemplateProvider>
  );
}

// Styled components
const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  overflow: hidden;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

export default App;
